import React, { useEffect } from 'react';
import {
  EOrderType,
  IOpenOrderData
} from '../../../../../../../store/ducks/long-steel-orders/types';
import { Container, Row, WarningText } from './styles';
import { formatCurrency } from '../../../../../../../utils/formatNumbers';
import moment from 'moment';
import ProductStatusDetailsTable from './ProductStatusDetailsTable';
import TrackingFlatSteel from './TrackingFlatSteel';
import {
  ECustomerTypeView,
  IUserOrderItemPreference
} from '../../../../../../../store/ducks/user/types';
import { uppercaseOnlyFirstLetter } from '../../../../../../../utils/formatStrings';
import TrackingLongSteel from './TrackingLongSteel';
import { Title } from '@gerdau/hefesto/Title';
import { KeyValueItem } from '@gerdau/hefesto-mais-web';
import { useDispatch } from 'react-redux';
import { sendTagging } from '../../../../../../../store/ducks/tagging/actions';
interface IExpandedOpenOrderRowContentProps {
  item: IOpenOrderData;
  userOrderPreference: IUserOrderItemPreference;
  customerViewType: ECustomerTypeView;
}

const ExpandedOpenOrderRowContent: React.FC<
  IExpandedOpenOrderRowContentProps
> = ({ item, userOrderPreference, customerViewType }) => {
  const isAvailableField = (field: string): boolean => {
    return field !== '' && field !== 'Indisponível';
  };
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      sendTagging({
        screenName: 'LG:MeusPedidos:AcosLongos:PedidosAbertos',
        itemName: 'expandable_card:ver_detalhes',
        itemDescription: 'meus_pedidos_acosLongos_pedidosAbertos_verDetalhes',
        event: 'click'
      })
    );
  }, [dispatch]);

  const canShowChangePriceAlert = (): boolean => {
    return (
      isAvailableField(item.conditions.totalValueWithoutTaxes) ||
      isAvailableField(item.conditions.totalValueWithoutIpi) ||
      isAvailableField(item.conditions.totalValueWithTaxes) ||
      isAvailableField(item.conditions.unitPriceWithoutTaxes) ||
      isAvailableField(item.conditions.unitPriceWithTaxes) ||
      isAvailableField(item.conditions.icms)
    );
  };

  const canShowOrderValuesBlock = (): boolean => {
    return (
      isAvailableField(item.conditions.totalValueWithoutTaxes) ||
      isAvailableField(item.conditions.totalValueWithoutIpi) ||
      isAvailableField(item.conditions.totalValueWithTaxes) ||
      isAvailableField(item.conditions.unitPriceWithoutTaxes) ||
      isAvailableField(item.conditions.unitPriceWithTaxes) ||
      isAvailableField(item.conditions.paymentTermsDescription) ||
      isAvailableField(item.conditions.icms)
    );
  };

  const getUnitPriceWithoutTaxesLabel = (): string => {
    let label = 'Preço unitário sem impostos';
    if (['61', '62'].includes(item.division)) {
      label = `${label} + frete`;
    }
    return label;
  };
  const unitPriceWithoutTaxesLabel = getUnitPriceWithoutTaxesLabel();

  return (
    <>
      {canShowOrderValuesBlock() && (
        <>
          {item.orderType === EOrderType.PLANOS && (
            <TrackingFlatSteel item={item} />
          )}

          {item.orderType === EOrderType.LONGOS && (
            <TrackingLongSteel item={item} />
          )}
        </>
      )}

      <Container>
        <div className="details">
          <Title level={3}>Detalhes do pedido</Title>

          <Row>
            {userOrderPreference.idOrder !== '0' ? (
              <KeyValueItem title="Número do pedido cliente">
                {item.customerOrder || ' - '}{' '}
              </KeyValueItem>
            ) : (
              <>
                <KeyValueItem title="Nº OV Gerdau">
                  {item.salesOrder}{' '}
                </KeyValueItem>
                <div style={{ minWidth: '23%' }}>
                  <KeyValueItem title="Nº item">
                    {item.salesOrderItem}{' '}
                  </KeyValueItem>
                </div>
              </>
            )}

            <KeyValueItem title="Material Gerdau">
              {userOrderPreference.idProduct !== '0'
                ? item.sku
                : item.customerSKU}
            </KeyValueItem>
          </Row>

          <KeyValueItem title="Criação do pedido">
            {moment(item.itemCreationDate, 'YYYY-MM-DD').format('DD/MM/YYYY')}
          </KeyValueItem>

          {customerViewType === ECustomerTypeView.EMITTER && (
            <KeyValueItem title="Recebedor">
              <span>
                {uppercaseOnlyFirstLetter(item.shipTo.name) +
                  ' - ' +
                  uppercaseOnlyFirstLetter(item.shipTo.address) +
                  ', ' +
                  uppercaseOnlyFirstLetter(item.shipTo.city) +
                  ' - ' +
                  item.shipTo.state}
              </span>
              <span>CNPJ: {item.shipTo.taxNumber}</span>
            </KeyValueItem>
          )}

          {(customerViewType === ECustomerTypeView.RECEIVER ||
            customerViewType === ECustomerTypeView.FABRICATION_RECEIVER) && (
            <KeyValueItem title="Emissor">
              <div>
                {uppercaseOnlyFirstLetter(item.soldTo.name) +
                  ' - ' +
                  uppercaseOnlyFirstLetter(item.soldTo.address) +
                  ', ' +
                  uppercaseOnlyFirstLetter(item.soldTo.city) +
                  ' - ' +
                  item.soldTo.state}
              </div>
              <div>CNPJ: {item.soldTo.taxNumber}</div>
            </KeyValueItem>
          )}

          <KeyValueItem title="Planta">{item.plantDescription}</KeyValueItem>
          <div>
            <Title level={3}>Status do produto</Title>
            <ProductStatusDetailsTable quantities={item.quantities} />
          </div>

          {canShowChangePriceAlert() && (
            <WarningText>
              * Atenção: Preços sujeitos a alterações sem aviso prévio.
            </WarningText>
          )}
        </div>

        <div className="values">
          {canShowOrderValuesBlock() && (
            <>
              <Title level={3}>Valores da ordem</Title>

              {isAvailableField(item.conditions.totalValueWithoutTaxes) && (
                <KeyValueItem title="Valor total sem impostos">
                  R$ {formatCurrency(item.conditions.totalValueWithoutTaxes)}
                </KeyValueItem>
              )}

              {isAvailableField(item.conditions.unitPriceWithoutTaxes) && (
                <KeyValueItem title={unitPriceWithoutTaxesLabel}>
                  R${' '}
                  {formatCurrency(item.conditions.unitPriceWithoutTaxes) +
                    ' / ' +
                    item.conditions.unitPriceWithoutTaxesUnit}
                </KeyValueItem>
              )}

              {isAvailableField(item.conditions.icms) && (
                <KeyValueItem
                  helperText={`ICMS alíquota ${item.conditions.aliquotIcms}%`}
                  title="ICMS"
                >
                  R$ {formatCurrency(item.conditions.icms)}
                </KeyValueItem>
              )}

              {isAvailableField(item.conditions.totalValueWithoutIpi) && (
                <KeyValueItem title="Valor total sem IPI">
                  R$ {formatCurrency(item.conditions.totalValueWithoutIpi)}
                </KeyValueItem>
              )}

              {isAvailableField(item.conditions.unitPriceWithTaxes) && (
                <KeyValueItem title="Preço unitário com impostos">
                  R${' '}
                  {formatCurrency(item.conditions.unitPriceWithTaxes) +
                    ' / ' +
                    item.conditions.unitPriceWithTaxesUnit}
                </KeyValueItem>
              )}

              {isAvailableField(item.conditions.totalValueWithTaxes) && (
                <KeyValueItem title="Valor total com impostos">
                  R$ {formatCurrency(item.conditions.totalValueWithTaxes)}
                </KeyValueItem>
              )}

              {isAvailableField(item.conditions.paymentTermsDescription) && (
                <KeyValueItem title="Forma de pagamento">
                  {item.conditions.paymentTermsDescription}
                </KeyValueItem>
              )}

              {['61', '62'].includes(item.division) &&
                isAvailableField(
                  item.conditions.unitPriceCustomerFlatSteel
                ) && (
                  <KeyValueItem title="Preço unitário base">
                    R${' '}
                    {formatCurrency(
                      item.conditions.unitPriceCustomerFlatSteel
                    ) +
                      ' / ' +
                      item.conditions.unitPriceWithTaxesUnit}
                  </KeyValueItem>
                )}
            </>
          )}
        </div>
      </Container>
    </>
  );
};

export default ExpandedOpenOrderRowContent;
